import {
  IconPlayerPause,
  IconPlayerPlay,
  IconVolume,
} from "@tabler/icons-react";
import { Duration } from "luxon";
import type { FC, ReactNode } from "react";
import { Button as AriaButton } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import {
  TranscriptSpeakerAvatar,
  type TranscriptSpeakerAvatarProps,
} from "./transcriptSpeakerAvatar.js";
import { useCapturedPlayPauseAction } from "./useCapturedPlayPauseAction.js";

export interface TranscriptMessagePlayback {
  /** The start time of this message, in seconds. */
  time: number;
  /** Whether the playhead is within this message, including while paused. */
  isCurrent: boolean;
  isPlaying: boolean;
  /** Plays from the start of this message. */
  onPlay: () => void;
  onPause: () => void;
}

export type TranscriptMessageProps = TranscriptSpeakerAvatarProps & {
  /** Omit for a message without audio, such as a chat message. */
  playback?: TranscriptMessagePlayback | undefined;
  children: ReactNode;
};

export const TranscriptMessage: FC<TranscriptMessageProps> = (props) => {
  const { playback, children, ...speakerProps } = props;
  const { speakerName } = speakerProps;

  const captured = useCapturedPlayPauseAction(playback?.isPlaying ?? false);
  // The current message follows live playback even if hover or focus captured
  // an earlier action.
  const action = playback?.isCurrent
    ? playback.isPlaying
      ? "pause"
      : "play"
    : captured.action;

  const avatar = <TranscriptSpeakerAvatar {...speakerProps} />;

  const content = (
    <div className="min-w-0 flex-1">
      <span
        className={twMerge(
          "block truncate text-xs font-semibold",
          playback && "pr-24",
        )}
      >
        {speakerName}
      </span>
      <div className="mt-1 space-y-2 text-sm leading-relaxed">{children}</div>
    </div>
  );

  if (!playback) {
    return (
      <div data-transcript-message className="-ml-2 flex items-start gap-1">
        <div className="mt-2 ml-2">{avatar}</div>
        <div className="min-w-0 flex-1 rounded-lg px-2 py-2">{content}</div>
      </div>
    );
  }

  const formattedTime = Duration.fromMillis(playback.time * 1000).toFormat(
    "h:mm:ss",
  );
  const PlayOrPauseIcon = action === "play" ? IconPlayerPlay : IconPlayerPause;

  return (
    <div data-transcript-message className="-ml-2 flex items-start gap-1">
      <div className="mt-2 ml-2">{avatar}</div>
      <div
        aria-current={playback.isCurrent ? "true" : undefined}
        className={twMerge(
          "group/message relative min-w-0 flex-1 rounded-lg transition-[background-color]",
          playback.isCurrent && "bg-gray-100",
        )}
      >
        <div className="rounded-lg px-2 py-2">{content}</div>
        <AriaButton
          className="absolute top-2 right-2 flex items-center gap-1"
          aria-label={
            action === "play" ? `Play from ${formattedTime}` : "Pause"
          }
          {...captured.captureProps}
          onPress={() => {
            if (action === "play") {
              playback.onPlay();
            } else {
              playback.onPause();
            }
            captured.onActionPerformed(action);
          }}
        >
          <div
            className={twMerge(
              "size-4 opacity-0 transition-opacity group-focus-within/message:opacity-100 group-hover/message:opacity-100",
              playback.isCurrent && "opacity-100 transition-none",
            )}
          >
            {playback.isPlaying ? (
              <IconVolume className="size-full text-indigo-500" />
            ) : (
              <PlayOrPauseIcon className="size-full text-muted-foreground" />
            )}
          </div>
          <span className="font-mono text-xs text-muted-foreground select-none">
            {formattedTime}
          </span>
        </AriaButton>
      </div>
    </div>
  );
};
